import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { StarIcon, UsersIcon, XIcon, BadgeCheckIcon } from "lucide-react";
import { mockupUrl } from "../data/images";
import { notificationNames } from "../data/offer";
type Popup = {
  id: number;
  kind: "purchase";
  name: string;
  minutes: number;
} | {
  id: number;
  kind: "rating";
  name: string;
} | {
  id: number;
  kind: "viewers";
  count: number;
};
const SEQUENCE: Popup["kind"][] = ["purchase", "purchase", "rating", "purchase", "viewers"];
const VISIBLE_MS = 3200;
export function SalesPopups() {
  const [active, setActive] = useState(false);
  const [popup, setPopup] = useState<Popup | null>(null);
  const counter = useRef(0);
  useEffect(() => {
    const el = document.getElementById("oferta");
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setActive(true);
        obs.disconnect();
      }
    }, {
      threshold: 0.1
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!active) return;
    let showTimer: ReturnType<typeof setTimeout>;
    let hideTimer: ReturnType<typeof setTimeout>;
    const show = () => {
      setPopup(makePopup(counter.current++));
      hideTimer = setTimeout(() => setPopup(null), VISIBLE_MS);
      const gap = [4000, 5000, 6000][Math.floor(Math.random() * 3)];
      showTimer = setTimeout(show, gap + 400);
    };
    show();
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [active]);
  return <div className="pointer-events-none fixed bottom-3 left-3 z-[60] w-[calc(100%-1.5rem)] max-w-[330px] sm:bottom-5 sm:left-5" aria-live="polite" data-id="element-84" data-mp="14ya19h25215i">
      <AnimatePresence data-id="element-85" data-mp="2gcnl8atrtwg9">
        {popup && <motion.div key={popup.id} initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3 rounded-2xl border border-blush-dark/50 bg-white p-3 pr-8 shadow-[0_18px_40px_-12px_rgba(59,34,25,0.45)]" data-id="element-86" data-mp="2gbch29weymom">
            <PopupVisual popup={popup} data-id="element-87" data-mp="1n07eycjqe9pk" />
            <PopupText popup={popup} data-id="element-88" data-mp="1mz4og5qbm9a6" />
            <button type="button" onClick={() => setPopup(null)} aria-label="Cerrar notificación" className="absolute right-2 top-2 rounded-full p-1 text-cocoa-light/60 transition-colors duration-150 hover:bg-blush-light hover:text-cocoa" data-id="element-89" data-mp="q9x4h25co88d">
              <XIcon className="h-3.5 w-3.5" data-id="element-90" data-mp="141lf10zyaon8" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function PopupVisual({
  popup
}: {
  popup: Popup;
}) {
  if (popup.kind === "purchase") return <img src={mockupUrl} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-blush-dark" data-id="element-91" data-mp="bjg0bb1bz47s" />;
  if (popup.kind === "rating") return <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gold-light/40" data-id="element-92" data-mp="3v7st15zwazxh">
        <StarIcon className="h-7 w-7 fill-gold text-gold" data-id="element-93" data-mp="3r5fxfaev4ief" />
      </div>;
  return <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-berry-light" data-id="element-94" data-mp="3m74od1we6bx9">
      <UsersIcon className="h-7 w-7 text-berry" data-id="element-95" data-mp="3h8b8x3xjoaug" />
      <span className="absolute right-1.5 top-1.5 flex h-2.5 w-2.5" data-id="element-96" data-mp="3h92sthhs4k4w">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-berry opacity-70" data-id="element-97" data-mp="3cg09e26pur2f" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-berry" data-id="element-98" data-mp="37irp9w20ccyj" />
      </span>
    </div>;
}
function PopupText({
  popup
}: {
  popup: Popup;
}) {
  if (popup.kind === "purchase") return <div className="text-sm leading-snug text-cocoa" data-id="element-99" data-mp="2ygqv5822m713">
        <p data-id="element-100" data-mp="2thnnrgymbopy">
          <b data-id="element-101" data-mp="2thxfb8zba3xr">{popup.name}</b> acaba de comprar <b data-id="element-102" data-mp="2tiz1o9ls2cus">Pack Dulce Saludable</b>
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-cocoa-light" data-id="element-103" data-mp="xeixi4n8vehi">
          <BadgeCheckIcon className="h-3.5 w-3.5 text-leaf" data-id="element-104" data-mp="12e5kv6wseb41" /> hace {popup.minutes} {popup.minutes === 1 ? "minuto" : "minutos"} · Compra verificada
        </p>
      </div>;
  if (popup.kind === "rating") return <div className="text-sm leading-snug text-cocoa" data-id="element-105" data-mp="snspskrcizjn">
        <p data-id="element-106" data-mp="sm99iu5owohv">
          <b data-id="element-107" data-mp="slzmnffxyxge">{popup.name}</b> acaba de calificar con 5 estrellas el <b data-id="element-108" data-mp="enrtzjpxuib1">Pack Dulce Saludable</b>
        </p>
        <div className="mt-1 flex gap-0.5" aria-label="5 estrellas" data-id="element-109" data-mp="iq6bo0hvfc0q">
          {Array.from({
        length: 5
      }).map((_, i) => <StarIcon key={i} className="h-3.5 w-3.5 fill-gold text-gold" data-id="element-110" data-mp="4r6lgoepmp2g" data-mpi={i} />)}
        </div>
      </div>;
  return <div className="text-sm leading-snug text-cocoa" data-id="element-111" data-mp="9q00wmdk4q59">
      <p data-id="element-112" data-mp="1z4o2rtoiupp1">
        <b className="text-berry" data-id="element-113" data-mp="1z3wd0zhlvm6o">{popup.count} personas</b> están comprando ahora mismo
      </p>
      <p className="mt-0.5 text-xs text-cocoa-light" data-id="element-114" data-mp="2440xt1pjr32p">Pack Dulce Saludable · En vivo</p>
    </div>;
}
function makePopup(index: number): Popup {
  const kind = SEQUENCE[index % SEQUENCE.length];
  const name = notificationNames[Math.floor(Math.random() * notificationNames.length)];
  if (kind === "purchase") return {
    id: index,
    kind,
    name,
    minutes: 1 + Math.floor(Math.random() * 15)
  };
  if (kind === "rating") return {
    id: index,
    kind,
    name
  };
  return {
    id: index,
    kind,
    count: 3 + Math.floor(Math.random() * 18)
  };
}